import React from "react";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCar, faClipboardList, faCog, faChevronRight, faHome, faTools } from '@fortawesome/free-solid-svg-icons'
import Translator from "app/components/common/Translator";
import { LoginUser } from "models/LoginUser";
import { UserRoleTypes } from "models/UserRole";
import { MobileNavMenuActions } from "redux/actions/MobileNavMenuActions";
import { CurrentSubscription } from "models/SubscriptionPlan";

interface Props {
    loginUser: LoginUser,
    currentSubscription: CurrentSubscription | null,
    navMenuToggle: boolean,
    mobileNavMenuToggle: boolean,
    hitNavMenu: typeof MobileNavMenuActions.hitNavMenu
}

interface State {
    catOpen: NavCategory[];
}

type NavElement = {
    title: string,
    url: string,
    role?: string, // defaults to PoolManager inside Settings
    needsTasks?: boolean,
    needsDefects?: boolean,
    hideWithDefects?: boolean,
    end?: boolean // highlight only on this exact url, not on the pages below it
}

type NavCategory = {
    title: string,
    url?: string,
    icon?: React.JSX.Element,
    elements?: NavElement[],
    isOpen: boolean
}

const icons = {
    home: <FontAwesomeIcon icon={faHome} className="c-navigation__icon"></FontAwesomeIcon>,
    machine: <FontAwesomeIcon icon={faCar} className="c-navigation__icon"></FontAwesomeIcon>,
    settings: <FontAwesomeIcon icon={faCog} className="c-navigation__icon"></FontAwesomeIcon>,
    tools: <FontAwesomeIcon icon={faTools} className="c-navigation__icon"></FontAwesomeIcon>,
    tasks: <FontAwesomeIcon icon={faClipboardList} className="c-navigation__icon"></FontAwesomeIcon>,
}

const nav: NavCategory[] = [
    {
        title: 'Home',
        url: '/',
        isOpen: false,
        icon: icons.home
    },
    {
        title: 'Machines',
        icon: icons.machine,
        isOpen: true,
        elements: [
            {
                title: 'Machines',
                url: '/Machines',
                role: UserRoleTypes.poolManager, // the route lives in the settings router
                end: true
            },
            {
                title: 'Daily report',
                url: '/Machines/dailyreport'
            },
            {
                // without defects the workshop only does maintenance, so it lives here
                title: 'Maintenance',
                url: '/Machines/workshop',
                hideWithDefects: true
            },
            {
                title: 'Upcoming Maintenance',
                url: '/Machines/UpcomingMaintenance'
            },
            {
                title: 'Machine History',
                url: '/Machines/histories'
            },
            {
                title: 'Machine Defects',
                url: '/Machines/defects',
                needsDefects: true
            },
            {
                title: 'Machine Statistics',
                url: '/Machines/statistics'
            },
        ]
    },
    {
        title: 'Workshop',
        icon: icons.tools,
        isOpen: true,
        elements: [
            {
                title: 'Workshop',
                url: '/Machines/workshop',
                needsDefects: true
            },
        ]
    },
    {
        title: 'Tasks',
        icon: icons.tasks,
        isOpen: true,
        elements: [
            {
                title: 'My Tasks',
                url: '/Tasks',
                needsTasks: true,
                end: true
            },
            {
                title: 'All Tasks',
                url: '/Tasks/all',
                needsTasks: true,
                role: UserRoleTypes.poolManager // the route lives in the settings router
            },
        ]
    },
    {
        title: 'Settings',
        icon: icons.settings,
        isOpen: false,
        elements: [
            {
                title: 'Machine Types',
                url: '/MachineTypes'
            },
            {
                title: 'Manufacturers',
                url: '/Manufacturers'
            },
            {
                title: 'Maintenance',
                url: '/Maintenances'
            },
            {
                title: 'Machine Maintenance',
                url: '/MachineMaintenances'
            },
            {
                title: 'Machine Parts',
                url: '/MachineParts'
            },
            {
                title: 'Fuel Types',
                url: '/FuelTypes'
            },
            {
                title: 'Units',
                url: '/Units'
            },
            {
                title: 'Users',
                url: '/Users'
            },
            {
                title: 'Tenant',
                url: '/Tenant'
            },
            {
                title: 'Subscriptions',
                url: '/Subscriptions',
                role: UserRoleTypes.admin
            },
        ]
    }
]

export class LeftBar extends React.Component<Props, State> {
    public readonly state: State = {
        catOpen: nav
    }

    toggleCategory(category: string) {
        this.setState(prevState => {
            let toggledCat = prevState.catOpen.find(tc => tc.title === category);
            if (toggledCat) {
                toggledCat.isOpen = !toggledCat.isOpen;
                return { catOpen: prevState.catOpen.map(co => co.title !== category ? co : toggledCat!) };
            }
        });
    }

    hasRole(role: string) {
        return !!this.props.loginUser.userData?.roles.find(r => r.name === role);
    }

    filterElement(cat: NavCategory, el: NavElement) {
        if (el.needsTasks && !this.props.currentSubscription?.subscriptionPlan.tasksEnabled) {
            return false;
        }
        if (el.needsDefects && !this.props.currentSubscription?.subscriptionPlan.defectsEnabled) {
            return false;
        }
        if (el.hideWithDefects && this.props.currentSubscription?.subscriptionPlan.defectsEnabled) {
            return false;
        }
        const role = el.role ?? (cat.title === "Settings" ? UserRoleTypes.poolManager : undefined);
        return !role || this.hasRole(role);
    }

    filterByPermission(cat: NavCategory) {
        return !cat.elements || cat.elements.some(el => this.filterElement(cat, el));
    }

    navMenu() {
        return (
            nav.filter(c => this.filterByPermission(c)).map(nav => {
                const isOpen = this.state.catOpen.find(co => co.title === nav.title && co.isOpen);
                return (
                    <React.Fragment key={nav.title}>
                        {nav.elements &&
                            <>
                                <li className="c-navigation__element" onClick={() => this.toggleCategory(nav.title)}>
                                    <div className="c-navigation__link"><span>{nav.icon}</span><Translator text={nav.title} /></div>
                                    <div className={"c-navigation__drop-arrow" + (isOpen ? " is-open" : "")}>
                                        <FontAwesomeIcon icon={faChevronRight}></FontAwesomeIcon>
                                    </div>
                                </li>
                                {isOpen &&
                                    <li>
                                        <ul>
                                            {nav.elements.filter(sub => this.filterElement(nav, sub)).map(sub => {
                                                return (
                                                    <li key={sub.url} className="c-navigation__element c-navigation__sub-element" onClick={this.props.hitNavMenu}>
                                                        <NavLink className={({ isActive }) => isActive ? "c-navigation__link is-active" : "c-navigation__link"} to={sub.url} end={sub.end}><Translator text={sub.title} /></NavLink>
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    </li>
                                }
                            </>
                        }
                        {nav.url &&
                            <li className="c-navigation__element" onClick={this.props.hitNavMenu}>
                                <NavLink className={({ isActive }) => isActive ? "c-navigation__link is-active" : "c-navigation__link"} to={nav.url}><span>{nav.icon}</span><Translator text={nav.title} /></NavLink>
                            </li>
                        }
                    </React.Fragment>
                )
            })
        )
    }

    render() {
        let className = this.props.navMenuToggle ? "c-leftbar" : "c-leftbar c-leftbar--collapsed"
        let classNameList = this.props.navMenuToggle ? "c-leftbar__list" : "c-leftbar__list c-leftbar__list--collapsed"
        if (this.props.mobileNavMenuToggle) {
            className += " c-leftbar__mobile"
            classNameList += " c-leftbar__mobile-list"
        } else {
            className += " c-leftbar__mobile c-leftbar__mobile--collapsed"
            classNameList += " c-leftbar__mobile-list c-leftbar__mobile-list--collapsed"
        }
        return (
            <>
                <div className={className}>
                    <ul className={classNameList}>
                        {this.navMenu()}
                    </ul>
                </div>
                <div className={"c-leftbar__backdrop" + (this.props.mobileNavMenuToggle ? " is-open" : "")} onClick={this.props.hitNavMenu} />
            </>
        );
    }
}
